import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { Link } from 'wouter';

export default function BillingUpgrade() {
  const { currentOrganizationId } = useOrganization();

  const { data } = useQuery({
    queryKey: ['/api/usage/storage/current', currentOrganizationId],
    queryFn: async () => {
      const url = `/api/usage/storage/current${currentOrganizationId ? `?organizationId=${currentOrganizationId}` : ''}`;
      const res = await fetch(url, { credentials: 'include', redirect: 'error' });
      if (!res.ok) throw new Error('Failed');
      return res.json() as Promise<{ tier?: string; tierLimitGb?: number; totalGb?: number; usagePercent?: number; tierTotalCost?: number; ratePerGb?: number }>
    },
    enabled: !!currentOrganizationId,
    staleTime: 0
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Upgrade Plans</h1>
        <a href="/settings" className="px-3 py-1.5 text-sm rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-700">← Back to Usage</a>
      </div>

      <div className="mb-4 rounded border border-yellow-700 bg-yellow-900/20 text-yellow-300 px-4 py-3 text-sm">
        HIPAA Notice: Billing pages inherit storage and audit controls appropriate for healthcare.{' '}
        <a href="/compliance" className="underline text-yellow-200">Compliance Details</a>
      </div>

      {/* Title shown in header above */}
      <p className="text-neutral-300 mb-6">Choose a storage plan that fits your organization. Current usage shown below.</p>

      {data && (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-neutral-400">Current tier</div>
            <div className="text-neutral-200 font-medium">{data.tier || 'Free'} · {(data.totalGb ?? 0).toFixed(2)} GB used ({(data.usagePercent ?? 0).toFixed(1)}%)</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <PlanCard name="Free" limitGb={10} price="$0/month" recommended={false} current={data?.tier === 'Free'} />
        <PlanCard name="Starter" limitGb={50} price="$25/month" recommended={false} current={data?.tier === 'Starter'} />
        <PlanCard name="Professional" limitGb={200} price="$79/month" recommended={true} current={data?.tier === 'Professional'} />
        <PlanCard name="Business" limitGb={500} price="$149/month" recommended={false} current={data?.tier === 'Business'} />
        <PlanCard name="Enterprise" limitGb={2000} price="$499/month" recommended={false} current={data?.tier === 'Enterprise'} />
      </div>

      {/* Add BoltOS Recovery Vault Section */}
      <div className="border-t border-neutral-800 pt-8">
        <h2 className="text-xl font-semibold mb-4">Add BoltOS Recovery Vault</h2>
        <div className="rounded-lg border border-teal-600 bg-neutral-900 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-medium text-teal-400 mb-2">Protect Your Organization's Data</h3>
              <p className="text-neutral-300 mb-4">
                BoltOS Recovery Vault provides enterprise-grade data protection with soft-delete, 
                recovery history, and HIPAA-compliant audit trails. Never lose critical files again.
              </p>
              <ul className="space-y-2 text-sm text-neutral-400 mb-4">
                <li className="flex items-center gap-2">
                  <span className="text-teal-500">✓</span> 30-day recovery window
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-teal-500">✓</span> Audit trail for all deletions
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-teal-500">✓</span> HIPAA-compliant storage
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-teal-500">✓</span> Dual-approval for permanent deletion
                </li>
              </ul>
              <div className="flex items-center gap-4">
                <Link href="/billing/recovery-tiers">
                  <button className="px-6 py-2 rounded bg-teal-600 hover:bg-teal-500 text-white font-medium">
                    View Recovery Plans
                  </button>
                </Link>
                <span className="text-neutral-400">Starting at $99/month</span>
              </div>
            </div>
            <div className="ml-6 text-right">
              <div className="text-4xl mb-2">🔐</div>
              <div className="text-xs text-neutral-500">Enterprise<br/>Protection</div>
            </div>
          </div>
        </div>
      </div>

      {/* Storage Manager Link */}
      <div className="mt-8 text-center">
        <Link href="/settings/storage-manager">
          <button className="text-neutral-400 hover:text-neutral-200 underline">
            Open BoltOS Storage Manager →
          </button>
        </Link>
      </div>
    </div>
  );
}

function PlanCard({ name, limitGb, price, recommended, current }: { name: string; limitGb: number; price: string; recommended?: boolean; current?: boolean }) {
  return (
    <div className={`rounded-lg border ${current ? 'border-teal-500 bg-teal-900/10' : recommended ? 'border-blue-500' : 'border-neutral-800'} bg-neutral-900 p-5`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-lg font-semibold text-neutral-100">{name}</div>
        {current && <span className="text-xs px-2 py-1 rounded bg-teal-600 text-white">CURRENT</span>}
        {!current && recommended && <span className="text-xs px-2 py-1 rounded bg-blue-600 text-white">RECOMMENDED</span>}
      </div>
      <div className="text-neutral-300 mb-1">Up to {limitGb} GB</div>
      <div className="text-2xl font-semibold mb-4">{price}</div>
      <button 
        className={`w-full px-4 py-2 rounded ${current ? 'bg-neutral-700 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500'} text-white`}
        disabled={current}
      >
        {current ? 'Current Plan' : `Select ${name}`}
      </button>
    </div>
  );
}


